// SeoHealthCard — live SEO / AI-discoverability checklist + IndexNow pinger.
import React, { useEffect, useState } from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Loader2, Radar, Send, RefreshCw } from 'lucide-react';
import { apiCall } from './_shared';

const ICON = { pass: [CheckCircle2, 'text-emerald-600'], warn: [AlertTriangle, 'text-amber-600'], fail: [XCircle, 'text-red-600'] };

// Sections on the same admin tab the check points to — 'Open →' scrolls there.
const ANCHORS = { hotel_desc: 'content-translations', attractions: 'attractions-editor', hours: 'hours-editor' };

// Google has no ping API any more: the only same-week route is "Request indexing" in Search Console. One tap per URL.
const SITE = 'https://larosa-negra.com';
const NEW_PAGES = [
  ['/sq/stay/halal-friendly-family-hotel-ulcinj', 'Albanian'], ['/stay/halal-friendly-family-hotel-ulcinj', 'English'], ['/de/stay/halal-friendly-family-hotel-ulcinj', 'German'],
  ['/sq/stay/hotel-ulqin', 'Albanian · hotel Ulqin'], ['/sq/stay/plazhi-i-madh-ulqin', 'Albanian · Plazhi i Madh'],
];
const inspectUrl = (path) => `https://search.google.com/search-console/inspect?resource_id=${encodeURIComponent('sc-domain:larosa-negra.com')}&id=${encodeURIComponent(SITE + path)}`;
const GoogleRequestIndexing = () => {
  const [custom, setCustom] = useState('');
  const path = custom.trim().replace(/^https?:\/\/[^/]+/, '');
  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50/60 px-3 py-2 mb-2 text-xs" data-testid="google-request-indexing">
      <div className="font-semibold text-slate-900 mb-1">Google — request indexing (same week instead of next month)</div>
      <div className="text-slate-600 mb-2">Google removed its ping endpoint; the sitemap is already submitted. For a new page, open it in Search Console and press <b>Request indexing</b> — one tap per URL:</div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {NEW_PAGES.map(([p, label]) => (
          <a key={p} href={inspectUrl(p)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-full border border-blue-300 bg-white px-2.5 py-1 font-semibold text-blue-800 hover:bg-blue-100" data-testid={`gsc-inspect-${p.replace(/[^a-z0-9]+/gi, '-')}`}>{label} ↗</a>
        ))}
      </div>
      <div className="flex gap-2">
        <input value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Any other page, e.g. /de/rooms" className="flex-1 border rounded px-2 py-1 bg-white" data-testid="gsc-custom-path" />
        <a href={path ? inspectUrl(path.startsWith('/') ? path : `/${path}`) : '#'} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center rounded px-3 py-1 font-semibold text-white ${path ? 'bg-slate-900 hover:bg-blue-700' : 'bg-slate-400 pointer-events-none'}`} data-testid="gsc-custom-open">Open in Search Console</a>
      </div>
    </div>
  );
};


const CheckRow = ({ c, onOpenTab }) => {
  const [Icon, color] = ICON[c.status];
  return (
    <li className="flex gap-3 py-2 border-b last:border-0 border-slate-100" data-testid={`seo-check-${c.id}`}>
      <Icon className={`w-5 h-5 shrink-0 mt-0.5 ${color}`} />
      <div className="min-w-0 flex-1">
        <div className="font-medium text-slate-900 text-sm">{c.title}</div>
        <div className="text-xs text-slate-600 break-words">{c.detail}</div>
        {c.fix && c.status !== 'pass' && <div className="text-xs text-amber-800 mt-0.5">→ {c.fix}</div>}
        {c.fix && c.status === 'pass' && <div className="text-xs text-slate-400 mt-0.5">{c.fix}</div>}
      </div>
      {c.tab && c.status !== 'pass' && onOpenTab && (
        <button type="button" onClick={() => { onOpenTab(c.tab); const anchor = ANCHORS[c.id]; if (anchor) setTimeout(() => document.getElementById(anchor)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 350); }} className="text-xs text-orange-600 font-semibold self-center whitespace-nowrap" data-testid={`seo-check-open-${c.id}`}>Open →</button>
      )}
    </li>
  );
};

export const SeoHealthCard = ({ onOpenTab }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ping, setPing] = useState('');
  const [err, setErr] = useState('');

  const load = () => {
    setBusy(true); setErr('');
    return apiCall(`/api/admin/seo/health?base=${encodeURIComponent(window.location.origin)}`).then(setData).catch((e) => setErr(e.message)).finally(() => setBusy(false));
  };
  useEffect(() => { load(); }, []);

  const pingAll = async () => {
    setPing('Pinging IndexNow…');
    try {
      const r = await apiCall('/api/admin/seo/indexnow', { method: 'POST', body: JSON.stringify({ all: true }) });
      setPing(r.ok ? `IndexNow accepted ${r.count} URLs (HTTP ${r.status}). Bing, Yandex, Naver and Seznam will recrawl them shortly.` : `IndexNow rejected the ping (HTTP ${r.status || '—'}): ${r.error}`);
      load();
    } catch (e) { setPing(`IndexNow failed: ${e.message}`); }
  };

  if (!data && busy) return <div className="border rounded-xl p-4 text-sm text-slate-500 flex items-center gap-2" data-testid="seo-health-loading"><Loader2 className="w-4 h-4 animate-spin" /> Checking SEO health…</div>;
  if (!data) return <div className="border rounded-xl p-4 text-sm text-red-700" data-testid="seo-health-error">SEO health unavailable: {err}</div>;

  const s = data.summary;
  const tone = s.fail ? 'border-red-200 bg-red-50/40' : s.warn ? 'border-amber-200 bg-amber-50/40' : 'border-emerald-200 bg-emerald-50/40';
  return (
    <div className={`border rounded-xl p-4 ${tone}`} data-testid="seo-health-card">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <Radar className="w-6 h-6 text-orange-500" />
        <div className="flex-1 min-w-[200px]">
          <div className="font-semibold text-slate-900">SEO & AI visibility health · <span data-testid="seo-health-score">{s.score}/100</span></div>
          <div className="text-xs text-slate-600">{s.pass} passed · {s.warn} to improve · {s.fail} failing · checked on {data.checked_host.replace(/^https?:\/\//, '')} · sitemap {data.sitemap_urls}/{data.expected_urls} URLs</div>
        </div>
        <button type="button" onClick={load} disabled={busy} className="inline-flex items-center gap-1 border rounded px-3 py-1 text-xs font-semibold bg-white disabled:opacity-60" data-testid="seo-health-refresh"><RefreshCw className={`w-3 h-3 ${busy ? 'animate-spin' : ''}`} /> Re-check</button>
        <button type="button" onClick={pingAll} disabled={!data.indexnow.configured} className="inline-flex items-center gap-1 bg-slate-900 text-white rounded px-3 py-1 text-xs font-semibold disabled:opacity-50" data-testid="seo-indexnow-ping-all"><Send className="w-3 h-3" /> Ping all pages (IndexNow)</button>
      </div>
      {ping && <div className="text-xs text-slate-700 mb-2" data-testid="seo-indexnow-result">{ping}</div>}
      <GoogleRequestIndexing />
      <ul className="bg-white/70 rounded-lg px-3">{data.checks.map((c) => <CheckRow key={c.id} c={c} onOpenTab={onOpenTab} />)}</ul>
      <div className="text-[11px] text-slate-500 mt-2">Google is fed by the sitemap + Search Console; IndexNow covers Bing (→ Copilot / ChatGPT search), Yandex, Naver and Seznam. AI assistants read llms.txt, llms-full.txt and the Key-facts blocks.</div>
    </div>
  );
};
